/**
 * Push Notification Utility
 * Handles push notification subscription, permission requests, and notification management
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

class PushNotificationManager {
  constructor() {
    this.subscription = null;
    this.swRegistration = null;
    this.vapidPublicKey = null; // Will be fetched from backend
  }

  /**
   * Fetch VAPID public key from backend
   * This is better than hardcoding because:
   * - Backend is source of truth
   * - No need to update frontend when rotating keys
   * - Works automatically for dev/staging/prod
   */
  async getVapidPublicKey() {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/push/vapid-public-key`);
      this.vapidPublicKey = response.data.publicKey;
      console.log('[Push Notifications] Fetched VAPID public key from backend');
      return this.vapidPublicKey;
    } catch (error) {
      console.error('[Push Notifications] Error fetching VAPID public key:', error);
      throw new Error('Failed to fetch VAPID public key from server');
    }
  }

  /**
   * Check if push notifications are supported
   */
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled() {
    return Notification.permission === 'granted';
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus() {
    if (!('Notification' in window)) {
      return 'unsupported';
    }
    return Notification.permission; // 'default', 'granted', or 'denied'
  }

  /**
   * Request notification permission from user
   */
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported in this browser');
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      throw new Error('Notification permission was previously denied. Please enable it in browser settings.');
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('[Push Notifications] Permission status:', permission);
      return permission;
    } catch (error) {
      console.error('[Push Notifications] Error requesting permission:', error);
      throw error;
    }
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe(userId, userEmail) {
    try {
      // First, ensure we have permission
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission not granted');
      }

      // Get VAPID public key from backend
      const vapidPublicKey = await this.getVapidPublicKey();

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      this.swRegistration = registration;

      // Check for existing subscription
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        // Create new subscription
        console.log('[Push Notifications] Creating new subscription...');
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
        });
      }

      this.subscription = subscription;

      // Send subscription to backend
      await this.sendSubscriptionToServer(subscription, userId, userEmail);

      console.log('[Push Notifications] Successfully subscribed:', subscription);
      return subscription;
    } catch (error) {
      console.error('[Push Notifications] Error subscribing:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(userId) {
    try {
      if (!this.swRegistration) {
        const registration = await navigator.serviceWorker.ready;
        this.swRegistration = registration;
      }

      const subscription = await this.swRegistration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from push manager
        await subscription.unsubscribe();
        
        // Remove subscription from server
        await this.removeSubscriptionFromServer(subscription, userId);
        
        this.subscription = null;
        console.log('[Push Notifications] Successfully unsubscribed');
        return true;
      }

      return false;
    } catch (error) {
      console.error('[Push Notifications] Error unsubscribing:', error);
      throw error;
    }
  }

  /**
   * Get current subscription
   */
  async getSubscription() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      this.subscription = subscription;
      return subscription;
    } catch (error) {
      console.error('[Push Notifications] Error getting subscription:', error);
      return null;
    }
  }

  /**
   * Check if user is currently subscribed
   */
  async isSubscribed() {
    const subscription = await this.getSubscription();
    return subscription !== null;
  }

  /**
   * Send subscription to backend
   */
  async sendSubscriptionToServer(subscription, userId, userEmail) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/push/subscribe`,
        {
          subscription: subscription.toJSON(),
          userId,
          userEmail,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('[Push Notifications] Subscription saved to server:', response.data);
      return response.data;
    } catch (error) {
      console.error('[Push Notifications] Error saving subscription to server:', error);
      throw error;
    }
  }

  /**
   * Remove subscription from backend
   */
  async removeSubscriptionFromServer(subscription, userId) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/push/unsubscribe`,
        {
          subscription: subscription.toJSON(),
          userId
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('[Push Notifications] Subscription removed from server:', response.data);
      return response.data;
    } catch (error) {
      console.error('[Push Notifications] Error removing subscription from server:', error);
      throw error;
    }
  }

  /**
   * Update notification preferences
   */
  async updatePreferences(userId, preferences) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/push/preferences`,
        {
          userId,
          preferences
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('[Push Notifications] Preferences updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('[Push Notifications] Error updating preferences:', error);
      throw error;
    }
  }

  /**
   * Get notification preferences
   */
  async getPreferences(userId) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/push/preferences/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('[Push Notifications] Error getting preferences:', error);
      throw error;
    }
  }

  /**
   * Send a test notification
   */
  async sendTestNotification(userId) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/push/test`,
        { userId },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      console.log('[Push Notifications] Test notification sent:', response.data);
      return response.data;
    } catch (error) {
      console.error('[Push Notifications] Error sending test notification:', error);
      throw error;
    }
  }

  /**
   * Show local notification (for testing or immediate notifications)
   */
  async showLocalNotification(title, options = {}) {
    if (!this.isSupported()) {
      console.warn('[Push Notifications] Notifications not supported');
      return;
    }

    if (Notification.permission !== 'granted') {
      console.warn('[Push Notifications] Notification permission not granted');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        body: options.body || '',
        icon: options.icon || '/favicon.png',
        badge: options.badge || '/favicon.png',
        tag: options.tag || 'dizee-notification',
        data: options.data || {},
        vibrate: options.vibrate || [200, 100, 200],
        requireInteraction: options.requireInteraction || false,
        actions: options.actions || []
      });
    } catch (error) {
      console.error('[Push Notifications] Error showing notification:', error);
    }
  }

  /**
   * Helper: Convert VAPID key to Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Listen for messages from service worker (e.g., notification clicks)
   */
  setupMessageListener(callback) {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('[Push Notifications] Message from service worker:', event.data);
        if (callback && typeof callback === 'function') {
          callback(event.data);
        }
      });
    }
  }

  /**
   * Get notification statistics
   */
  async getNotificationStats(userId) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/push/stats/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('[Push Notifications] Error getting stats:', error);
      return null;
    }
  }
}

// Create singleton instance
const pushNotificationManager = new PushNotificationManager();

// Export both the class and instance
export default pushNotificationManager;
export { PushNotificationManager };

